import { useState } from 'react';
import { useEditorStore, useSelectedLayer, useActivePage } from '../../store/editorStore';
import TextProperties from './TextProperties';
import ImageProperties from './ImageProperties';
import ShapeProperties from './ShapeProperties';
import CTAProperties from './CTAProperties';
import InteractiveProperties from './InteractiveProperties';
import FormProperties from './FormProperties';
import TransformProperties from './TransformProperties';
import AnimationProperties from './AnimationProperties';
import PageProperties from './PageProperties';
import AudioProperties from './AudioProperties';

const panelStyle = {
  background: 'var(--t-surface)',
  borderLeft: '1px solid var(--t-border)',
};
const dividerStyle = { borderBottom: '1px solid var(--t-border)' };

export default function PropertiesPanel() {
  const [view, setView] = useState('layer');
  const selectedLayer = useSelectedLayer();
  const selectedLayerIds = useEditorStore((s) => s.selectedLayerIds);
  const deselectAll = useEditorStore((s) => s.deselectAll);
  const activePage = useActivePage();

  const panelWidth = 'w-80';
  const hasSelection = selectedLayerIds.length > 0;

  if (!activePage) {
    return (
      <div className={`${panelWidth} flex items-center justify-center`} style={panelStyle}>
        <p className="text-xs text-center px-4" style={{ color: 'var(--t-muted)' }}>
          Select a story to start editing
        </p>
      </div>
    );
  }

  // When a layer is selected, show a tab bar: Layer | Page
  if (hasSelection) {
    const effectiveView = view === 'layer' ? 'layer' : 'page';

    return (
      <div className={`${panelWidth} flex flex-col overflow-hidden`} style={panelStyle}>
        {/* Tab switcher */}
        <div className="flex shrink-0" style={dividerStyle}>
          {[{ key: 'layer', label: 'Layer' }, { key: 'page', label: 'Page' }].map(({ key, label }) => (
            <button
              key={key}
              onClick={() => setView(key)}
              className="flex-1 transition-colors"
              style={{
                padding: '8px 0 7px',
                fontSize: '12px',
                fontWeight: effectiveView === key ? 600 : 400,
                fontFamily: 'Inter, sans-serif',
                letterSpacing: '-0.1px',
                color: effectiveView === key ? 'var(--brand-primary)' : 'var(--t-muted)',
                borderTop: 'none', borderLeft: 'none', borderRight: 'none',
                borderBottom: effectiveView === key ? '2px solid var(--brand-primary)' : '2px solid transparent',
                background: 'transparent',
                cursor: 'pointer',
              }}
            >
              {label}
            </button>
          ))}
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden">
          {effectiveView === 'page' ? (
            <PageProperties page={activePage} />
          ) : selectedLayerIds.length > 1 ? (
            <div className="p-3">
              <h3 className="panel-label mb-2">Multiple Selection</h3>
              <p className="text-xs" style={{ color: 'var(--t-muted)' }}>{selectedLayerIds.length} elements selected</p>
            </div>
          ) : selectedLayer ? (
            <>
              <div className="px-3 py-2.5" style={dividerStyle}>
                <h3 className="panel-label">{selectedLayer.name}</h3>
                <p className="capitalize mt-0.5" style={{ color: 'var(--t-muted)', fontSize: '11px', fontFamily: 'Inter, sans-serif' }}>
                  {selectedLayer.data.type} layer
                </p>
              </div>
              <div style={dividerStyle}>
                <TransformProperties layer={selectedLayer} />
              </div>
              {renderTypeProperties(selectedLayer)}
              <div style={{ borderTop: '1px solid var(--t-border)', marginTop: '4px' }}>
                <AnimationProperties layer={selectedLayer} />
              </div>
            </>
          ) : null}
        </div>
      </div>
    );
  }

  // No selection — show page properties directly
  return (
    <div className={`${panelWidth} overflow-y-auto`} style={panelStyle}>
      <div className="p-2" style={dividerStyle}>
        <h3 className="panel-label">Page Properties</h3>
      </div>
      <PageProperties page={activePage} />
    </div>
  );
}

function renderTypeProperties(selectedLayer) {
  if (!selectedLayer) return null;
  const type = selectedLayer.data.type;
  switch (type) {
    case 'text':        return <TextProperties layer={selectedLayer} />;
    case 'image':       return <ImageProperties layer={selectedLayer} />;
    case 'shape':       return <ShapeProperties layer={selectedLayer} />;
    case 'cta':         return <CTAProperties layer={selectedLayer} />;
    case 'interactive': return <InteractiveProperties layer={selectedLayer} />;
    case 'form':        return <FormProperties layer={selectedLayer} />;
    case 'audio':       return <AudioProperties layer={selectedLayer} />;
    default:            return null;
  }
}
